// Prompt builder for the "generate company description" buttons across the company forms.
//
// Keep this fast and grounded: tell the model EXACTLY what to do so it doesn't run
// open-ended, multi-call web research (which made it slow). With a website it reads that one
// site and writes from it; without one it writes from the entered fields and does no research
// at all. The single-tool-call instruction also matters for model reliability — see the
// DEFAULT_COMPLETIONS_MODEL note in the backend constants.

interface CompanyDescriptionParams {
  name: string;
  industry?: string;
  website?: string;
  location?: string;
  size?: string;
  maxWords?: number;
}

export function buildCompanyDescriptionPrompt({
  name,
  industry,
  website,
  location,
  size,
  maxWords = 800,
}: CompanyDescriptionParams): string {
  const researchInstruction = website
    ? `Use the research_subject_provide_summary tool EXACTLY ONCE to read this company's website (${website}), then write the description from that content only. Do not perform any other research or call any tool more than once.`
    : `No website was provided, so do NOT use any research tools. Write the description from the details below only.`;

  const details = [
    `Name: ${name}`,
    `Industry: ${industry || "Not specified"}`,
    `Website: ${website || "Not specified"}`,
  ];
  // Location/size are only collected on some forms; include them when supplied.
  if (location !== undefined) details.push(`Location: ${location || "Not specified"}`);
  if (size !== undefined) details.push(`Size: ${size || "Not specified"}`);

  return `Write a company description in under ${maxWords} words. Focus on what the company does, their target market, and value proposition. Write in the third person, professionally.

${researchInstruction}

Company details:
${details.join("\n")}

Return ONLY the description text — no preamble, headings, or commentary.`;
}
